import { openReportWithAuth } from "../util";
import { cleApi, getCleApiPrefix } from "./client";
import type { CleFieldValidationMap } from "./models/fieldValidation";
import type { ReviewThreadListItemModel } from "./models/reviewThreads";
import axios from "axios";

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export type CourseRelevanceCategory =
  | "legal_subject"
  | "negotiation"
  | "management"
  | "communication"
  | "rhetoric"
  | "business_understanding"
  | "approvable_format"
  | "non_approvable"
  | "unclear";

export type CourseRelevanceConfidence = "high" | "medium" | "low";

export type ProviderAttestation =
  "signature" | "provider_stamp" | "other_mark" | "none";

export interface EvidenceAttachmentModel {
  id: string;
  fileName: string;
  sizeBytes: number;
  createdAt: string;
  createdByUserName: string | null;
}

export interface CourseCertificateEvidenceModel {
  id: string;
  documentId: string;
  fileName: string;
  evidenceType: string;

  modelDecisionBy: string;
  isReported: boolean;
  reportedAt: string | null;

  status: string;
  aiDecision: string | null;
  aiRejectionReason: string | null;
  /**
   * AI'ens begrundelse for relevansvurderingen bag `isLegalCourseCertificate`.
   * Skrivebeskyttet: en sagsbehandler retter boolean'en, aldrig disse. null for
   * manuelle beviser og for analyser fra før vurderingen fandtes.
   */
  aiRelevanceCategory?: CourseRelevanceCategory | null;
  aiRelevanceConfidence?: CourseRelevanceConfidence | null;
  /** En til to sætninger på dansk. */
  aiRelevanceReasoning?: string | null;
  /** Hvad AI'en fandt som dokumentation fra udbyderen (kriterie 3). */
  aiProviderAttestation?: ProviderAttestation | null;
  /** Én sætning på dansk om, hvad AI'en så, fx "JUC-stempel på underskriftslinjen". */
  aiProviderAttestationDescription?: string | null;
  humanReviewRequestedAt: string | null;
  isLegalCourseCertificate: boolean | null;
  courseTitle: string | null;
  courseProvider: string | null;
  courseDescription: string | null;
  courseDate: string | null;
  /** Last day of the course. null means a one-day course, not an unknown end. */
  courseEndDate: string | null;
  /** Total duration of the course in minutes — not a time of day. */
  courseTime: number | null;
  numberOfLessons: number | null;
  signature: boolean | null;
  /**
   * Udbyderens logo/stempel på beviset. Kriterie 3 er opfyldt af underskrift
   * ELLER logo, så de to deler én række under nøglen `signature`.
   */
  providerLogo?: boolean | null;
  participantName: string | null;
  /**
   * Ejerens navn, da beviset blev uploadet — det navn `participantName` blev
   * valideret imod. Fryses ved oprettelsen og opdateres aldrig, så efter et
   * navneskift er det den eneste kilde til, hvad sammenligningen faktisk brugte.
   */
  ownerDisplayNameSnapshot: string | null;
  fieldValidation: CleFieldValidationMap | null;
  /** Supplerende dokumentation (bilag), medlemmet har vedhæftet. Ikke
   * beviser: de analyseres ikke og giver ingen lektioner. */
  attachments?: EvidenceAttachmentModel[] | null;

  ownerUserId: string;
  createdByUserName: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;

  reported_in_injunction_id: string | null;
  reported_in_external_period_id: string | null;
  reviewThread: ReviewThreadListItemModel | null;
}

/** @deprecated Prefer CourseCertificateEvidenceModel */
export type CourseCertificateDocumentModel = CourseCertificateEvidenceModel;

export interface CourseCertificateEvidenceListModel {
  id: string;
  documentId: string;
  fileName: string;
  evidenceType: string;

  status: string;

  isLegalCourseCertificate: boolean | null;
  courseTitle: string | null;
  courseProvider: string | null;
  courseDescription: string | null;
  courseDate: string | null;
  /** Last day of the course. null means a one-day course, not an unknown end. */
  courseEndDate: string | null;
  numberOfLessons: number | null;
  signature: boolean | null;
  providerLogo?: boolean | null;

  isReported: boolean;
  createdAt: string;
  updatedAt: string;
  ownerUserId: string;
}

export interface GetCourseCertificatesParams {
  userId: string;
  page?: number;
  pageSize?: number;
}

export interface GetCourseCertificateByIdParams {
  userId: string;
  id: string;
}

/** Request body for PUT users/{userId}/course-certificates/{evidenceId} */
export interface UpdateCourseCertificateRequest {
  course_title?: string | null;
  course_provider?: string | null;
  course_description?: string | null;
  course_date?: string | null;
  /** Last day of the course; null clears it back to "one-day course". */
  course_end_date?: string | null;
  /**
   * Total duration of the course in minutes, across all days of the course.
   * The API rejects anything above MAX_COURSE_DURATION_MINUTES.
   */
  course_time?: number | null;
  number_of_lessons?: number | null;
  is_legal_course_certificate?: boolean | null;
  signature?: boolean | null;
  provider_logo?: boolean | null;
  participant_name?: string | null;
}

export interface UpdateCourseCertificateParams {
  userId: string;
  id: string;
  body: UpdateCourseCertificateRequest;
}

export interface GetCourseCertificatesByPeriodParams {
  userId: string;
  periodId: string;
}

export interface GetCourseCertificatesByInjunctionParams {
  userId: string;
  injunctionId: string;
}

export interface GetCourseCertificatesByImpositionParams {
  userId: string;
  impositionId: string;
}

export const getCourseCertificates = ({
  userId,
  page = 1,
  pageSize = 20,
}: GetCourseCertificatesParams): Promise<
  PaginatedResponse<CourseCertificateEvidenceModel>
> => {
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  return cleApi.get<PaginatedResponse<CourseCertificateEvidenceModel>>(
    `users/${encodeURIComponent(userId)}/course-certificates?${query.toString()}`,
  );
};

export const getCourseCertificateById = ({
  userId,
  id,
}: GetCourseCertificateByIdParams): Promise<CourseCertificateEvidenceModel> =>
  cleApi.get<CourseCertificateEvidenceModel>(
    `users/${encodeURIComponent(userId)}/course-certificates/${encodeURIComponent(
      id,
    )}`,
  );

export const updateCourseCertificate = ({
  userId,
  id,
  body,
}: UpdateCourseCertificateParams): Promise<CourseCertificateEvidenceModel> =>
  cleApi.put<CourseCertificateEvidenceModel>(
    `users/${encodeURIComponent(userId)}/course-certificates/${encodeURIComponent(id)}`,
    body,
  );

export const getCourseCertificatesByPeriodId = ({
  userId,
  periodId,
}: GetCourseCertificatesByPeriodParams): Promise<
  CourseCertificateEvidenceModel[]
> =>
  cleApi.get<CourseCertificateEvidenceModel[]>(
    `users/${encodeURIComponent(userId)}/course-certificates/by-period/${encodeURIComponent(periodId)}`,
  );

export const getCourseCertificatesByInjunctionId = ({
  userId,
  injunctionId,
}: GetCourseCertificatesByInjunctionParams): Promise<
  CourseCertificateEvidenceModel[]
> =>
  cleApi.get<CourseCertificateEvidenceModel[]>(
    `users/${encodeURIComponent(userId)}/course-certificates/by-injunction/${encodeURIComponent(injunctionId)}`,
  );

export const getCourseCertificatesByImpositionId = ({
  userId,
  impositionId,
}: GetCourseCertificatesByImpositionParams): Promise<
  CourseCertificateEvidenceModel[]
> =>
  cleApi.get<CourseCertificateEvidenceModel[]>(
    `users/${encodeURIComponent(userId)}/course-certificates/by-imposition/${encodeURIComponent(impositionId)}`,
  );

export const openCourseCertificateDownload = ({
  userId,
  id,
}: GetCourseCertificateByIdParams): Promise<void> =>
  openReportWithAuth(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/course-certificates/${encodeURIComponent(id)}/download`,
  );

export interface DownloadCourseCertificateAttachmentParams {
  userId: string;
  id: string;
  attachmentId: string;
}

export const openCourseCertificateAttachmentDownload = ({
  userId,
  id,
  attachmentId,
}: DownloadCourseCertificateAttachmentParams): Promise<void> =>
  openReportWithAuth(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/course-certificates/${encodeURIComponent(
      id,
    )}/attachments/${encodeURIComponent(attachmentId)}/download`,
  );

export const getCourseCertificateAttachmentBlob = async ({
  userId,
  id,
  attachmentId,
}: DownloadCourseCertificateAttachmentParams): Promise<Blob> => {
  const response = await axios.get<Blob>(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/course-certificates/${encodeURIComponent(
      id,
    )}/attachments/${encodeURIComponent(attachmentId)}/download`,
    { responseType: "blob" },
  );

  return response.data;
};

export const getCourseCertificatePdfBlob = async ({
  userId,
  id,
}: GetCourseCertificateByIdParams): Promise<Blob> => {
  const response = await axios.get<Blob>(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/course-certificates/${encodeURIComponent(id)}/download`,
    { responseType: "blob" },
  );

  return response.data;
};
